import { useState, useMemo, useCallback } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatDateTime, timeAgo, formatDate, paginate, truncate } from '../utils/helpers';

// ─── Configs ──────────────────────────────────────────────────────────────────
const TICKET_STATUS = {
  open:          { label: 'Ouvert',          cls: 'badge-yellow', icon: '🟡' },
  in_progress:   { label: 'En cours',        cls: 'badge-blue',   icon: '🔵' },
  waiting_user:  { label: 'Attente client',  cls: 'badge-purple', icon: '🟣' },
  resolved:      { label: 'Résolu',          cls: 'badge-green',  icon: '🟢' },
  closed:        { label: 'Fermé',           cls: 'badge-gray',   icon: '⚫' },
};

const TICKET_PRIORITY = {
  low:    { label: 'Faible',  cls: 'badge-gray',   color: 'var(--text3)' },
  medium: { label: 'Moyen',   cls: 'badge-blue',   color: 'var(--blue)' },
  high:   { label: 'Élevé',   cls: 'badge-orange', color: 'var(--orange)' },
  urgent: { label: 'Urgent',  cls: 'badge-red',    color: 'var(--red)' },
};

const TICKET_CATEGORY = {
  technical: { label: 'Technique',  icon: '🔧' },
  order:     { label: 'Commande',   icon: '📦' },
  payment:   { label: 'Paiement',   icon: '💳' },
  account:   { label: 'Compte',     icon: '👤' },
  other:     { label: 'Autre',      icon: '📋' },
};

// ─── StatCard ─────────────────────────────────────────────────────────────────
const SupportStatCard = ({ icon, label, value, color, sub }) => (
  <div style={{
    background: 'var(--bg2)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '18px 20px', position: 'relative', overflow: 'hidden',
  }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color }} />
    <div style={{ fontSize: 26, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 26, fontWeight: 800, fontFamily: 'Syne', color }}>{value ?? '—'}</div>
    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── ThreadMessage ────────────────────────────────────────────────────────────
const ThreadMessage = ({ msg }) => {
  const isAdmin    = msg.sender_role === 'admin';
  const isInternal = msg.is_internal;
  return (
    <div style={{
      display: 'flex', flexDirection: isAdmin ? 'row-reverse' : 'row',
      gap: 10, marginBottom: 16,
    }}>
      <Avatar name={msg.sender_name || (isAdmin ? 'Admin' : 'Client')} size="sm" />
      <div style={{ maxWidth: '72%' }}>
        <div style={{
          background: isInternal
            ? 'rgba(240,160,74,0.08)'
            : isAdmin ? 'rgba(232,240,74,0.08)' : 'var(--bg3)',
          border: `1px solid ${
            isInternal ? 'rgba(240,160,74,0.25)'
            : isAdmin ? 'rgba(232,240,74,0.2)' : 'var(--border)'}`,
          borderRadius: 12,
          padding: '10px 14px',
        }}>
          {isInternal && (
            <div style={{ fontSize: 10, color: 'var(--orange)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              🔒 Note interne (non visible par le client)
            </div>
          )}
          <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)' }}>{msg.message}</div>
        </div>
        <div style={{
          fontSize: 11, color: 'var(--text3)', marginTop: 4,
          textAlign: isAdmin ? 'right' : 'left',
        }}>
          {msg.sender_name} · {timeAgo(msg.created_at)}
        </div>
      </div>
    </div>
  );
};

// ─── TicketDetailModal ────────────────────────────────────────────────────────
const TicketDetailModal = ({ ticket, onClose, onUpdated }) => {
  const toast = useToast();
  const { data: detail, loading, error, refetch } = useApi(
    () => adminApi.getTicketMessages(ticket.ticket_id),
    [ticket.ticket_id],
  );

  const [reply,      setReply]      = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending,    setSending]    = useState(false);
  const [newStatus,  setNewStatus]  = useState(ticket.status);
  const [resolution, setResolution] = useState('');
  const [updating,   setUpdating]   = useState(false);

  const messages = detail?.data?.messages || [];

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await adminApi.sendTicketMessage(ticket.ticket_id, {
        message:     reply.trim(),
        is_internal: isInternal,
      });
      setReply('');
      setIsInternal(false);
      refetch();
      toast.success('Message envoyé ✓');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    try {
      await adminApi.updateTicket(ticket.ticket_id, {
        status:     newStatus,
        ...(newStatus === 'resolved' && resolution ? { resolution } : {}),
      });
      toast.success('Ticket mis à jour ✓');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const sc = TICKET_STATUS[ticket.status]   || { label: ticket.status, cls: 'badge-gray' };
  const pc = TICKET_PRIORITY[ticket.priority] || { label: ticket.priority, cls: 'badge-gray', color: 'var(--text2)' };
  const cc = TICKET_CATEGORY[ticket.category] || { label: ticket.category, icon: '📋' };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${ticket.ticket_number}`}
      size="xl"
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, minHeight: 500 }}>

        {/* Colonne gauche : fil de messages */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{
            background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '14px 16px', marginBottom: 16,
          }}>
            <div style={{ fontWeight: 700, fontFamily: 'Syne', fontSize: 15, marginBottom: 4 }}>
              {ticket.subject}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
              {cc.icon} {cc.label} · {ticket.client_name || 'Client inconnu'} · {formatDate(ticket.created_at)}
            </div>
            {ticket.order_id && (
              <div style={{ fontSize: 12, color: 'var(--blue)' }}>
                📦 Commande liée : #{ticket.order_id}
              </div>
            )}
          </div>

          {/* Thread */}
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 340, marginBottom: 16, paddingRight: 4 }}>
            {loading ? <Loading text="Chargement des messages..." />
              : error ? <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>
              : messages.length === 0 ? (
                <EmptyState icon="💬" title="Aucun message" text="Soyez le premier à répondre." />
              ) : messages.map((m, i) => <ThreadMessage key={i} msg={m} />)
            }
          </div>

          {/* Zone de réponse */}
          <div style={{
            background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '12px 14px',
          }}>
            <textarea
              className="form-textarea"
              placeholder={isInternal ? '🔒 Note interne (non visible par le client)...' : 'Votre réponse...'}
              value={reply}
              onChange={e => setReply(e.target.value)}
              style={{
                minHeight: 80, marginBottom: 10,
                border: isInternal ? '1px solid rgba(240,160,74,0.4)' : undefined,
                background: isInternal ? 'rgba(240,160,74,0.04)' : undefined,
              }}
              onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') handleSend(); }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text2)' }}>
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={e => setIsInternal(e.target.checked)}
                  style={{ accentColor: 'var(--orange)', width: 14, height: 14 }}
                />
                🔒 Note interne
              </label>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSend}
                disabled={sending || !reply.trim()}
              >
                {sending ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '↑ Envoyer (Ctrl+Entrée)'}
              </button>
            </div>
          </div>
        </div>

        {/* Colonne droite : infos + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Émetteur du ticket */}
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
              Émetteur
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Avatar name={ticket.client_name || 'Inconnu'} size="md" />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{ticket.client_name || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {ticket.client_role === 'laverie' ? '🏢 Laverie'
                    : ticket.client_role === 'livreur' ? '🛵 Livreur'
                    : '👤 Client'}
                </div>
              </div>
            </div>
            {[
              { icon: '📧', label: 'Email',     value: ticket.client_email },
              { icon: '📞', label: 'Téléphone', value: ticket.client_phone },
              { icon: '🆔', label: 'ID',        value: ticket.client_id ? `#${ticket.client_id}` : null },
            ].map((r, i) => r.value ? (
              <div key={i} className="info-row" style={{ padding: '5px 0' }}>
                <span className="info-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {r.icon} {r.label}
                </span>
                <span className="info-value" style={{ fontSize: 12 }}>
                  {r.label === 'Email'
                    ? <a href={`mailto:${r.value}`} style={{ color: 'var(--accent)' }}>{r.value}</a>
                    : r.label === 'Téléphone'
                      ? <a href={`tel:${r.value}`} style={{ color: 'var(--accent)' }}>{r.value}</a>
                      : r.value}
                </span>
              </div>
            ) : null)}
          </div>

          {/* Statut & Priorité */}
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              Détails du ticket
            </div>
            {[
              { l: 'Statut',    v: <Badge className={sc.cls}>{sc.icon} {sc.label}</Badge> },
              { l: 'Priorité',  v: <Badge className={pc.cls}>{pc.label}</Badge> },
              { l: 'Catégorie', v: `${cc.icon} ${cc.label}` },
              { l: 'Créé le',   v: formatDateTime(ticket.created_at) },
              { l: 'Mis à jour',v: timeAgo(ticket.updated_at) },
              ...(ticket.order_id ? [{ l: 'Commande', v: `📦 #${ticket.order_id}` }] : []),
              ...(ticket.resolution ? [{ l: 'Résolution', v: ticket.resolution }] : []),
            ].map((r, i) => (
              <div key={i} className="info-row" style={{ padding: '6px 0' }}>
                <span className="info-label">{r.l}</span>
                <span className="info-value">{r.v}</span>
              </div>
            ))}
          </div>

          {/* Changer le statut */}
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              Modifier le statut
            </div>
            <select
              className="form-select"
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              style={{ marginBottom: 10 }}
            >
              {Object.entries(TICKET_STATUS).map(([k, v]) => (
                <option key={k} value={k}>{v.icon} {v.label}</option>
              ))}
            </select>
            {newStatus === 'resolved' && (
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">Résolution</label>
                <textarea
                  className="form-textarea"
                  placeholder="Décrire comment le problème a été résolu..."
                  value={resolution}
                  onChange={e => setResolution(e.target.value)}
                  style={{ minHeight: 70 }}
                />
              </div>
            )}
            <button
              className="btn btn-primary btn-sm btn-full"
              onClick={handleUpdateStatus}
              disabled={updating || newStatus === ticket.status}
            >
              {updating ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Appliquer'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
export const Support = () => {
  const { data: statsRaw, refetch: refetchStats } = useApi(() => adminApi.getSupportStats());

  const [statusFilter,   setStatusFilter]   = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search,         setSearch]         = useState('');
  const [page,           setPage]           = useState(1);
  const [selected,       setSelected]       = useState(null);

  const { data: raw, loading, error, refetch } = useApi(
    () => adminApi.getSupportTickets(),
  );

  const tickets = raw?.data || [];
  const stats   = statsRaw?.data;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tickets.filter(t => {
      const matchS  = statusFilter   === 'all' || t.status   === statusFilter;
      const matchP  = priorityFilter === 'all' || t.priority === priorityFilter;
      const matchC  = categoryFilter === 'all' || t.category === categoryFilter;
      const matchQ  = !q
        || t.ticket_number?.toLowerCase().includes(q)
        || t.subject?.toLowerCase().includes(q)
        || t.client_name?.toLowerCase().includes(q);
      return matchS && matchP && matchC && matchQ;
    });
  }, [tickets, statusFilter, priorityFilter, categoryFilter, search]);

  const paged = paginate(filtered, page, 12);

  const handleUpdated = useCallback(() => {
    refetch();
    refetchStats();
  }, [refetch, refetchStats]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Stats */}
      {stats && (
        <div className="stats-grid section-gap">
          <SupportStatCard icon="🎫" label="Total tickets"   value={stats.total}       color="var(--blue)"   />
          <SupportStatCard icon="🟡" label="Ouverts"         value={stats.open}        color="var(--accent)" sub={`${stats.in_progress ?? 0} en cours`} />
          <SupportStatCard icon="🟢" label="Résolus"         value={stats.resolved}    color="var(--green)"  />
          <SupportStatCard icon="🚨" label="Urgents"         value={stats.urgent}      color="var(--red)"    sub="Priorité urgente" />
        </div>
      )}

      {/* Filtres */}
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            placeholder="N° ticket, sujet, client..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select className="filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">Statut: Tous</option>
          {Object.entries(TICKET_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
        <select className="filter-select" value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}>
          <option value="all">Priorité: Toutes</option>
          {Object.entries(TICKET_PRIORITY).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select className="filter-select" value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
          <option value="all">Catégorie: Toutes</option>
          {Object.entries(TICKET_CATEGORY).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} tickets</span>
        <button className="btn btn-ghost btn-sm" onClick={() => { refetch(); refetchStats(); }}>↺</button>
      </div>

      {/* Tableau */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <Loading text="Chargement des tickets..." />
        ) : error ? (
          <ApiError message={error} onRetry={refetch} />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>N° Ticket</th>
                    <th>Sujet</th>
                    <th>Client</th>
                    <th>Catégorie</th>
                    <th>Priorité</th>
                    <th>Statut</th>
                    <th>Créé</th>
                    <th>Dernière activité</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.items.map(t => {
                    const sc = TICKET_STATUS[t.status]     || { label: t.status, cls: 'badge-gray', icon: '⚪' };
                    const pc = TICKET_PRIORITY[t.priority] || { label: t.priority, cls: 'badge-gray', color: 'var(--text2)' };
                    const cc = TICKET_CATEGORY[t.category] || { label: t.category, icon: '📋' };
                    return (
                      <tr
                        key={t.ticket_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelected(t)}
                      >
                        <td>
                          <span className="text-mono" style={{ fontSize: 12 }}>{t.ticket_number}</span>
                        </td>
                        <td>
                          <div className="td-main">{truncate(t.subject, 38)}</div>
                          {t.order_id && <div className="td-sub">📦 #{t.order_id}</div>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Avatar name={t.client_name} size="sm" />
                            <div>
                              <div className="td-main">{t.client_name || '—'}</div>
                              <div className="td-sub">
                                {t.client_role === 'laverie' ? '🏢 Laverie'
                                  : t.client_role === 'livreur' ? '🛵 Livreur'
                                  : '👤 Client'}
                              </div>
                              {t.client_phone && (
                                <div className="td-sub">📞 {t.client_phone}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontSize: 13 }}>{cc.icon} {cc.label}</span>
                        </td>
                        <td>
                          <span style={{
                            fontSize: 11, fontWeight: 700, color: pc.color,
                            padding: '3px 8px', borderRadius: 6,
                            background: `${pc.color}1a`,
                          }}>
                            {pc.label}
                          </span>
                        </td>
                        <td>
                          <Badge className={sc.cls}>{sc.icon} {sc.label}</Badge>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text2)' }}>
                          {formatDate(t.created_at)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text3)' }}>
                          {timeAgo(t.updated_at)}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <button className="btn btn-primary btn-sm" onClick={() => setSelected(t)}>
                            Ouvrir →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {paged.items.length === 0 && !loading && (
                <EmptyState icon="🎫" title="Aucun ticket" text="Aucun ticket ne correspond aux filtres sélectionnés." />
              )}
            </div>

            {paged.total > 0 && (
              <div style={{ padding: '0 16px 16px' }}>
                <Pagination {...paged} onPage={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal détail ticket */}
      {selected && (
        <TicketDetailModal
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
};
